import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import type { ProductAvailabilityReportDTO } from "../calculationsApi";
import { checkCalculationAvailability } from "../calculationsApi";

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly products: { readonly productId: string; readonly date: string }[];
  readonly participantsNumber: number;
}

const AvailabilityReportDialog: FC<Props> = ({ open, onClose, products, participantsNumber }) => {
  const queryKey = products.map((p) => `${p.productId}:${p.date}`).join(",");
  const { data, isLoading } = useQuery({
    queryKey: ["availability", queryKey, participantsNumber],
    queryFn: ({ signal }) => checkCalculationAvailability(products, participantsNumber, signal),
    enabled: open && products.length > 0,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>Dostępność pokoi</DialogTitle>
      <DialogContent>
        {isLoading && <LinearProgress />}
        {data?.products.map((product) => (
          <ProductSection key={product.productId} product={product} />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ProductSectionProps {
  readonly product: ProductAvailabilityReportDTO;
}

const ProductSection: FC<ProductSectionProps> = ({ product }) => {
  const columnDates = Array.from(
    new Set(product.options.flatMap((option) => option.availabilities.map((av) => av.date))),
  ).sort();

  return (
    <>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {product.productName}
      </Typography>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Pokój</TableCell>
            {columnDates.map((date) => (
              <TableCell key={date} sx={{ whiteSpace: "nowrap" }}>
                {date}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {product.options.map((option) => (
            <TableRow key={option.optionId}>
              <TableCell>{option.optionName}</TableCell>
              {columnDates.map((date) => {
                const av = option.availabilities.find((a) => a.date === date);
                return <TableCell key={date}>{av !== undefined ? av.quantity : "—"}</TableCell>;
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default AvailabilityReportDialog;
